'use client';
import React, { useState } from 'react';
import { X, Star, Loader2, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ReviewModalProps {
  workerName: string;
  workerId: string;
  bookingRef: string;
  onClose: () => void;
  onSubmitted?: () => void;
}

interface RatingState {
  overall: number;
  punctuality: number;
  quality: number;
  attitude: number;
}

const CATEGORIES = [
  { key: 'punctuality' as const, label: 'Punctuality', description: 'Arrived on time and kept to schedule' },
  { key: 'quality' as const, label: 'Quality of Service', description: 'Standard of care provided' },
  { key: 'attitude' as const, label: 'Attitude', description: 'Professionalism and demeanour' },
];

function StarPicker({ value, onChange, size = 24 }: { value: number; onChange: (v: number) => void; size?: number }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={`sp-${star}`}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform duration-100 hover:scale-110 focus:outline-none"
          aria-label={`${star} star`}
        >
          <Star
            size={size}
            className={(hovered || value) >= star ? 'fill-current' : ''}
            style={{ color: (hovered || value) >= star ? 'var(--amber)' : 'var(--border)' }}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewModal({ workerName, workerId, bookingRef, onClose, onSubmitted }: ReviewModalProps) {
  const [ratings, setRatings] = useState<RatingState>({ overall: 0, punctuality: 0, quality: 0, attitude: 0 });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRating = (key: keyof RatingState, value: number) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const isValid = ratings.overall > 0 && ratings.punctuality > 0 && ratings.quality > 0 && ratings.attitude > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be signed in to leave a review.');

      const { error: insertError } = await supabase.from('reviews').insert({
        worker_id: workerId,
        reviewer_id: user.id,
        booking_ref: bookingRef,
        rating_overall: ratings.overall,
        rating_punctuality: ratings.punctuality,
        rating_quality: ratings.quality,
        rating_attitude: ratings.attitude,
        comment: comment.trim() || null,
      });

      if (insertError) throw new Error(insertError.message);
      setSubmitted(true);
      onSubmitted?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'var(--card)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-extrabold text-lg" style={{ color: 'var(--navy)' }}>
            Review {workerName}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-muted"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--success-bg)' }}>
              <CheckCircle size={32} style={{ color: 'var(--success)' }} />
            </div>
            <h3 className="font-extrabold text-xl mb-2" style={{ color: 'var(--navy)' }}>Review submitted!</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
              Thank you for your feedback. It helps families find the right care.
            </p>
            <button onClick={onClose} className="btn-primary px-8">Done</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Overall rating */}
            <div>
              <p className="text-sm font-bold mb-1" style={{ color: 'var(--navy)' }}>Overall rating</p>
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>How would you rate your overall experience?</p>
              <StarPicker value={ratings.overall} onChange={(v) => setRating('overall', v)} size={32} />
            </div>

            {/* Specific categories */}
            <div className="space-y-4">
              <p className="text-sm font-bold" style={{ color: 'var(--navy)' }}>Specific ratings</p>
              {CATEGORIES.map((cat) => (
                <div key={cat.key} className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl" style={{ background: 'var(--muted)' }}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy)' }}>{cat.label}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{cat.description}</p>
                  </div>
                  <StarPicker value={ratings[cat.key]} onChange={(v) => setRating(cat.key, v)} size={20} />
                </div>
              ))}
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm font-bold block mb-1" style={{ color: 'var(--navy)' }}>
                Comments <span className="font-normal" style={{ color: 'var(--muted-foreground)' }}>(optional)</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={600}
                placeholder="Share your experience — what stood out, what could be improved..."
                className="w-full rounded-xl px-4 py-3 text-sm resize-none border focus:outline-none focus:ring-2 transition-all"
                style={{
                  background: 'var(--background)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
              />
              <p className="text-xs text-right mt-1" style={{ color: 'var(--muted-foreground)' }}>{comment.length}/600</p>
            </div>

            {error && (
              <div className="rounded-xl px-4 py-3 text-sm" style={{ background: 'var(--destructive-bg, #FEF2F2)', color: 'var(--destructive, #DC2626)' }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pb-1">
              <button
                onClick={onClose}
                className="flex-1 btn-outline"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid || submitting}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  'Submit Review'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
